import React from 'react';

import { FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import PropTypes from 'prop-types';

import { VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import { useForwardedRef } from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import { DATE_PICKER_VARIANTS } from './consts';
import { defaultRenderTrigger } from './helpers/defaultRenderers';
import useDateRangePicker from './hooks/useDateRangePicker';
import Footer from './layout/Footer';
import DatePickerOverlay from './layout/Overlay';
import Trigger from './layout/Trigger';
import TriggerWrapper from './layout/TriggerWrapper';
import type { DatePickerCommonArgs, DateRangePickerArgs } from './types';

type DateRangePickerProps = DateRangePickerArgs &
  DatePickerCommonArgs &
  Omit<StylableComponent<HTMLDivElement>, 'onChange'>;
/**
 * The Date Range Picker is a basic component that allows the user to pick a date range
 * Date pickers allow the selection of specific dates and years. It displays a default
 * text field and a dropdown calendar appears when a user taps on the text field.
 */
const DateRangePicker = React.forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      style,
      className,
      variant = DATE_PICKER_VARIANTS.FIELD,
      container = document.body,
      renderTrigger = defaultRenderTrigger,
      overlayProps = {},
      initialValueSelected = true,
      ...props
    },
    ref,
  ) => {
    const target = useForwardedRef(ref);
    const [shouldShowSelection, setShouldShowSelection] =
      React.useState<boolean>(initialValueSelected);
    const dateRangePickerData = useDateRangePicker({
      variant,
      target,
      setShouldShowSelection,
      ...props,
    });
    const {
      context,
      open,
      targetProps,
      placement,
      getTriggerProps,
      arrowProps,
      overlayContainerProps,
    } = dateRangePickerData;

    return (
      <>
        <TriggerWrapper
          className={className}
          css={style !== null ? style : undefined}
          {...targetProps}
        >
          {renderTrigger({ ...getTriggerProps() })}
        </TriggerWrapper>
        <FloatingPortal root={container}>
          {open && (
            <FloatingFocusManager context={context} modal={false}>
              <div {...overlayContainerProps}>
                {variant === DATE_PICKER_VARIANTS.ICON && (
                  <Overlay.Arrow
                    placement={placement}
                    color={theme.tokens.colors.background.primary.value}
                    {...arrowProps}
                  />
                )}
                <DatePickerOverlay
                  range
                  double={props.double}
                  timeZone={props.timeZone}
                  calendarProps={props.calendarProps}
                  onMonthChange={props.onMonthChange}
                  showOutsideDays={props.showOutsideDays}
                  isDateSelectable={props.isDateSelectable}
                  yearsToDisplay={props.yearsToDisplay}
                  firstDayOfWeek={props.firstDayOfWeek}
                  tooltipContentForDate={props.tooltipContentForDate}
                  hasEventForDate={props.hasEventForDate}
                  renderFooter={props.renderFooter}
                  renderFooterActions={props.renderFooterActions}
                  minDate={dateRangePickerData.minDate}
                  maxDate={dateRangePickerData.maxDate}
                  startDate={dateRangePickerData.startDate}
                  endDate={dateRangePickerData.endDate}
                  toggleOverlay={dateRangePickerData.toggleOverlay}
                  onDateSelected={dateRangePickerData.onDateSelected}
                  resetSelection={dateRangePickerData.resetSelection}
                  shouldShowSelection={shouldShowSelection}
                  {...overlayProps}
                />
              </div>
            </FloatingFocusManager>
          )}
        </FloatingPortal>
      </>
    );
  },
);

DateRangePicker.displayName = 'DateRangePicker';

DateRangePicker.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Variant of the field could be icon or field */
  variant: PropTypes.oneOf(Object.values(DATE_PICKER_VARIANTS)),
  /** A selected start date for a calendar with range data (should be used with range=true) */
  startDate: PropTypes.instanceOf(Date),
  /** A selected end date for a calendar with range data (should be used with range=true) */
  endDate: PropTypes.instanceOf(Date),
  /** The initial start date for calendar with date range select */
  initialStartDate: PropTypes.instanceOf(Date),
  /** The initial end date for calendar with date range select */
  initialEndDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates after minDate, overrides `disablePastDates` */
  minDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates before maxDate, overrides `disableFutureDates` */
  maxDate: PropTypes.instanceOf(Date),
  /** Whether we should disable dates before today */
  disablePastDates: PropTypes.bool,
  /** Whether we should disable dates after today */
  disableFutureDates: PropTypes.bool,
  /** A callback function called when the selected date(s) has been changed */
  onChange: PropTypes.func,
  /** A callback function called when the active months have changed (arrow click, month/year selection) */
  onMonthChange: PropTypes.func,
  /** Handler to manually check if date is selectable */
  isDateSelectable: PropTypes.func,
  /** The format to display the date with */
  format: PropTypes.oneOf(Object.values(VALID_DATE_FORMATS)),
  /** Whether picker should be open initially */
  initialOpen: PropTypes.bool,
  /** Controlled picker open state */
  open: PropTypes.bool,
  /** Whether the we should display double date picker */
  double: PropTypes.bool,
  /** Whether the input should display in error state */
  error: PropTypes.bool,
  /** Disable all functionality */
  disabled: PropTypes.bool,
  /** First day of week to display */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  /** Fill the blanks with days from prev/next month */
  showOutsideDays: PropTypes.bool,
  /** Whether we should allow range start and end to be the same date */
  allowSameStartAndEndDate: PropTypes.bool,
  /** Passed to the field, placeholder to display when there is no value - when passed in range mode, it will affect both start and end dates */
  placeholder: PropTypes.string,
  /** Passed to the field, placeholder to display when there is no startDate. this prop overrides `placeholder` */
  startDatePlaceholder: PropTypes.string,
  /** Passed to the field, placeholder to display when there is no endDate. this prop overrides `placeholder` */
  endDatePlaceholder: PropTypes.string,
  /** Props passed to DatePickerField or Button (when variant is icon) */
  triggerProps: PropTypes.object,
  /** Props passed to DatePickerOverlay */
  overlayProps: PropTypes.object,
  /* Props passed to each calendar */
  calendarProps: PropTypes.object,
  /** Render custom footer, executed in DatePickerOverlay */
  renderFooter: PropTypes.func,
  /** Render custom footer actions, executed in DatePickerOverlay */
  renderFooterActions: PropTypes.func,
  /** Render custom footer, executed in DateRangePicker */
  renderTrigger: PropTypes.func,
  /** Determines if Day Cell should render an event dot */
  hasEventForDate: PropTypes.func,
  /** Tooltip content for Day Cell */
  tooltipContentForDate: PropTypes.func,
  /** How much years to display under and above current year, defaults to +-20 */
  yearsToDisplay: PropTypes.number,
  /** Specific time zone for this DateRangePicker, overriding the user's time zone */
  timeZone: PropTypes.string,
  /** Get the container elemenet for the overlay element */
  container: PropTypes.any,
  /** A callback function called when DateRangePicker overlay is being hidden (closed) */
  onHide: PropTypes.func,
  /** Options that modify how the calendar overlay can shift (see floating-ui documentation https://floating-ui.com/docs/shift) */
  shiftArgs: PropTypes.any,
  /** Options that modify how the calendar overlay can flip (see floating-ui documentation https://floating-ui.com/docs/flip) */
  flipArgs: PropTypes.any,
  /** When true, initialValue will be selected, when false calendar will be opened to initialValue */
  initialValueSelected: PropTypes.bool,
};

export default Object.assign(DateRangePicker, {
  VARIANTS: DATE_PICKER_VARIANTS,
  VALID_FORMATS: VALID_DATE_FORMATS,
  Footer,
  Trigger,
});
